/**
 * thebigplan WTT baseline client (§5e) — the real planned working timetable, read
 * cross-service from thebigplan via /api/research/baseline. When unavailable the
 * caller falls back to the sales-derived baseline and flags it on screen
 * (MASTER_PLAN §6.6 — baseline provenance is explicit, never silent).
 */
import type { Departure, SetFormation } from './types'
import { canonicalDiagramId } from './diagrams'
import { toMin } from './demand'

export interface BaselineWorking {
  id: string
  pattern_code: string
  headcode: string
  direction: string
  diagram: string | null
  traction: string | null
  set: string | null
  forms: string | null
  ext: boolean
  /** Per-station "HH:MM" times, keyed PIC/LEV/GOA/GRO/WHB (only where the train calls). */
  times: Record<string, string>
  /** Raw thebigplan timing tokens (9 slots per direction) — the TTBuilder-format
   *  grid needs arr AND dep per station plus the Newtondale 'r'/'ns' tokens. */
  t_json?: string
}

export interface BaselinePattern {
  code: string
  name: string
  season: string | null
  colour: string | null
  workings: number
}

export interface BaselineTimetable {
  name: string
  status: string
  version: string
  effective_from: string
  effective_to: string
}

/** One calendar date's timetable colour (thebigplan reports.vw_TimetableDay). */
export interface BaselineDay {
  date: string
  colour: string
  run: boolean
}

export interface BaselineWtt {
  available: boolean
  source?: string
  reason?: string
  timetable?: BaselineTimetable
  patterns?: BaselinePattern[]
  selectedPattern?: string | null
  workings?: BaselineWorking[]
  /** Every date's colour — the date→pattern truth (W0) and calendar paint (W3). */
  days?: BaselineDay[]
}

export async function loadBaseline(pattern?: string, forDate?: string, dayClass?: string): Promise<BaselineWtt> {
  const params = new URLSearchParams()
  if (pattern) params.set('pattern', pattern)
  // The modelled date: the API serves the timetable whose effective window
  // COVERS it (status only breaks ties), so an August session gets the summer
  // WTT — not whichever one happens to be published. With a day-class it also
  // picks the month's majority-colour pattern server-side, one round trip.
  if (forDate) params.set('for', forDate)
  if (dayClass) params.set('dayClass', dayClass)
  const qs = params.toString()
  const q = qs ? `?${qs}` : ''
  try {
    const r = await fetch(`/api/research/baseline${q}`)
    if (!r.ok) return { available: false, reason: `baseline request failed (${r.status})` }
    return (await r.json()) as BaselineWtt
  } catch (e) {
    return { available: false, reason: String((e as Error)?.message ?? e) }
  }
}

// ---------------------------------------------------------------------------
//  W0 — the real WTT, scored: decode workings into engine Departures.
// ---------------------------------------------------------------------------

const N_ORDER = ['PIC', 'LEV', 'GOA', 'GRO', 'WHB'] as const
const ST_IDX: Record<string, number> = { PIC: 0, LEV: 1, GOA: 2, GRO: 3, WHB: 4 }

/**
 * Measured set capacities for the codes thebigplan writes on workings —
 * mirrors stock.DEFAULT_SETS (Chris, 2026-08-19). thebigplan writes the
 * Internal rake as A, M or A/M depending on the day; they are one Internal
 * set to the model.
 */
const WTT_SETS: Record<string, { seats: number; seatsOffPeak: number; coaches: number; norm: string }> = {
  'W1':  { seats: 407, seatsOffPeak: 320, coaches: 8, norm: 'W1' },
  'W2':  { seats: 407, seatsOffPeak: 320, coaches: 7, norm: 'W2' },
  'A':   { seats: 279, seatsOffPeak: 220, coaches: 5, norm: 'INT' },
  'M':   { seats: 279, seatsOffPeak: 220, coaches: 5, norm: 'INT' },
  'A/M': { seats: 279, seatsOffPeak: 220, coaches: 5, norm: 'INT' },
  'INT': { seats: 279, seatsOffPeak: 220, coaches: 5, norm: 'INT' },
  'R':   { seats: 90,  seatsOffPeak: 90,  coaches: 0, norm: 'R' },
}

export interface WttObserved {
  deps: Departure[]
  /** Dining/charter (and Northern-connection) workings excluded from
   *  daily-services scoring — their revenue is a separate P&L. */
  excluded: string[]
  /** Real vehicles and rakes in daily-services traffic, for costing. */
  carriages: number
  setsInTraffic: number
}

/**
 * The real working timetable as engine Departures — measured departure AND
 * arrival times (so the D1 chainer sees the real 15–20 min turns), thebigplan's
 * own diagram identity (canonicalised), traction pinned where the WTT names it,
 * and MEASURED seats from the named carriage set in the given formation.
 *
 * Scope is DAILY SERVICES: Pullman dining workings (set P / diagram TBA) are
 * excluded — their revenue lives in Diner & Charter, not in the daily-services
 * demand the engine pours — and Northern-connection rows are not our trains.
 */
export function wttToDeps(workings: BaselineWorking[], formation: SetFormation): WttObserved {
  const deps: Departure[] = []
  const excluded: string[] = []
  const setsUsed = new Map<string, number>()
  for (const w of workings) {
    const traction = (w.traction ?? '').toLowerCase()
    if (w.set === 'P' || w.diagram === 'TBA' || traction.includes('advised') || traction.includes('northern')) {
      excluded.push(w.headcode)
      continue
    }
    const north = w.direction !== 'S'
    const order = north ? N_ORDER : [...N_ORDER].reverse()
    const timed = order.filter((st) => !!w.times[st])
    if (timed.length < 2) continue
    const o = ST_IDX[timed[0]]
    const d = ST_IDX[timed[timed.length - 1]]
    const depMin = toMin(w.times[timed[0]])
    const arrMin = toMin(w.times[timed[timed.length - 1]])
    if (!Number.isFinite(depMin) || !Number.isFinite(arrMin)) continue
    const set = w.set ? WTT_SETS[w.set] : undefined
    if (set) setsUsed.set(set.norm, set.coaches)
    const setPin = set?.norm
    const pin = traction === 'steam' ? 'steam' as const
      : traction === 'diesel' ? 'diesel' as const
      : traction.includes('railcar') ? 'railcar' as const
      : undefined
    deps.push({
      id: w.headcode,
      direction: north ? 'Northbound' : 'Southbound',
      time: w.times[timed[0]],
      minutes: depMin,
      enabled: true,
      coaches: set ? set.coaches : 6,
      capacity: set ? (formation === 'offpeak' ? set.seatsOffPeak : set.seats) : undefined,
      o, d,
      arrMin,
      diagramId: w.diagram ? canonicalDiagramId(w.diagram) : undefined,
      // The WTT names its rake on every working — pin it so the set plan
      // reproduces the railway rather than re-inventing the allocation.
      ...(setPin ? { setPin } : {}),
      ...(pin ? { tractionPin: pin } : {}),
    })
  }
  const carriages = [...setsUsed.values()].reduce((t, c) => t + c, 0)
  const setsInTraffic = [...setsUsed.entries()].filter(([, c]) => c > 0).length
  return { deps, excluded, carriages, setsInTraffic }
}

/** Pullman formation: 83 individual seats + 5 coupés × 4 (agg.Pullman_Services
 *  capacity convention) — dining covers, not daily-services seats. */
const PULLMAN_CAPACITY = 103

/**
 * The month's PULLMAN dining workings as engine Departures — the set-P rows
 * `wttToDeps` deliberately excludes from daily-services scoring. The Pullmans
 * still traffic through the designed timetable (Chris, 2026-09-02: "ensure the
 * Pullmans are still able to traffic through the new timetable — you need to
 * factor them in"), so their real paths are decoded here for the timetable
 * listings, the workbook, and the crossing/headway check against the design.
 * They carry NO daily-services demand — their covers and revenue are shown from
 * the actual Pullman sales, outside the daily-services P&L.
 */
export function pullmanWttDeps(workings: BaselineWorking[]): Departure[] {
  const deps: Departure[] = []
  for (const w of workings) {
    if (w.set !== 'P') continue
    const north = w.direction !== 'S'
    const order = north ? N_ORDER : [...N_ORDER].reverse()
    const timed = order.filter((st) => !!w.times[st])
    if (timed.length < 2) continue
    const o = ST_IDX[timed[0]]
    const d = ST_IDX[timed[timed.length - 1]]
    const depMin = toMin(w.times[timed[0]])
    const arrMin = toMin(w.times[timed[timed.length - 1]])
    if (!Number.isFinite(depMin) || !Number.isFinite(arrMin)) continue
    const traction = (w.traction ?? '').toLowerCase()
    const pin = traction === 'steam' ? 'steam' as const
      : traction === 'diesel' ? 'diesel' as const
      : undefined
    deps.push({
      id: `pullman-${w.headcode}`,
      direction: north ? 'Northbound' : 'Southbound',
      time: w.times[timed[0]],
      minutes: depMin,
      enabled: true,
      coaches: 6,
      capacity: PULLMAN_CAPACITY,
      o, d,
      arrMin,
      pullman: true,
      diagramId: w.diagram && w.diagram !== 'TBA' ? canonicalDiagramId(w.diagram) : undefined,
      ...(pin ? { tractionPin: pin } : {}),
    })
  }
  return deps.sort((a, b) => a.minutes - b.minutes)
}

/**
 * A Pullman SERVICE is one dining train out and back — Grosmont to Pickering
 * and return (Chris, 2026-09-03: "Pullman runs from Grosmont to Pickering and
 * back — 1 service"). The WTT draws it as two set-P legs; this pairs each
 * outbound leg with the return that leaves its arrival station afterwards, so
 * every surface counts and names SERVICES, never legs. Legs that pair with
 * nothing (a one-way positioning path) count as a service each, honestly.
 */
export function pullmanServices(deps: Departure[]): { services: number; legs: Departure[]; description: string } {
  const legs = [...deps].filter((d) => d.pullman).sort((a, b) => a.minutes - b.minutes)
  const paired = new Set<string>()
  let services = 0
  let description = ''
  const names = ['Pickering', 'Levisham', 'Goathland', 'Grosmont', 'Whitby']
  for (const out of legs) {
    if (paired.has(out.id)) continue
    services += 1
    paired.add(out.id)
    const back = legs.find((r) => !paired.has(r.id) && r.o === out.d && r.minutes >= (out.arrMin ?? out.minutes))
    if (back) paired.add(back.id)
    if (!description) description = `${names[out.o] ?? out.o} to ${names[out.d] ?? out.d}${back ? ' and back' : ' (one way)'}`
  }
  return { services, legs, description }
}

/**
 * Which pattern actually runs for a month × day-class, from the date→colour
 * calendar — the majority colour of the matching running dates, as a TT-<COLOUR>
 * pattern code. No day-class→pattern guessing (W0): thebigplan says.
 */
export function patternForSlice(days: BaselineDay[], monthKey: string, dayClass: string): string | null {
  const m = /^(\d{4})-(\d{2})$/.exec(monthKey)
  if (!m) return null
  const wantDow = (dow: number): boolean =>
    dayClass === 'Sun' ? dow === 0
    : dayClass === 'Sat' ? dow === 6
    : dayClass.startsWith('Wkdy') ? dow >= 1 && dow <= 5
    : true // 'Week' — the whole week's majority
  const counts = new Map<string, number>()
  for (const d of days) {
    if (!d.run || !d.date.startsWith(`${m[1]}-${m[2]}`)) continue
    const dow = new Date(`${d.date}T12:00:00Z`).getUTCDay()
    if (!wantDow(dow)) continue
    const c = d.colour.trim().toUpperCase()
    // Diner/gala days have no standard pattern to score — skip in the majority.
    if (!c || c === 'DINER') continue
    counts.set(c, (counts.get(c) ?? 0) + 1)
  }
  const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]
  return top ? `TT-${top[0]}` : null
}
